import { useTranslation } from '@unifyapps/i18n/client';
import { fallbackLng } from '@unifyapps/i18n/settings';
import ChevronRight from '@unifyapps/icons/outline/ChevronRight';
import SvgTranslate02 from '@unifyapps/icons/outline/Translate02';
import { Popover } from '@unifyapps/ui/components/Popover';
import { Stack } from '@unifyapps/ui/components/Stack';
import { Toggle } from '@unifyapps/ui/components/Toggle';
import { Typography } from '@unifyapps/ui/components/Typography';
import { clsx } from 'clsx';
import { useCallback } from 'react';
import { EMPTY_OBJECT } from '../consts/empty';
import useSetLanguage from '../no-code/hooks/useGlobalBlockActions/actions/setLanguage/useSetLanguage';
import { useGlobalAppStateStore } from '../no-code/stores/app/GlobalAppStateStore';
import InterfaceHelper from '../no-code/stores/InterfaceStore/helper/InterfaceHelper';

function LanguageOption({
  label,
  isActive,
  onSelect,
  value,
}: {
  value: string;
  label: string;
  isActive: boolean;
  onSelect: (value: string) => void;
}) {
  return (
    <Stack
      alignItems="center"
      className={clsx(
        'gap-x-md cursor-pointer rounded-lg px-md py-sm transition-colors hover:bg-primary_hover',
        isActive ? 'bg-brand-primary' : '',
      )}
      direction="row"
      onClick={() => {
        onSelect(value);
      }}
    >
      <Typography className="flex-1" color="text-primary" variant="text-sm" weight="light">
        {label}
      </Typography>
    </Stack>
  );
}

export function LanguageSwitcher({
  localeOptions,
  direction,
}: {
  localeOptions?: { value: string; label: string }[];
  direction?: 'ltr' | 'rtl';
}) {
  const { t } = useTranslation(['blocks']);

  const { onSetLanguage } = useSetLanguage();
  const onSelect = useCallback(
    (locale: string) => {
      void onSetLanguage({
        action: { payload: { language: locale } },
        actionContext: EMPTY_OBJECT,
      });
    },
    [onSetLanguage],
  );
  const currentLocale = useGlobalAppStateStore().hooks.useUserContext().locale || '';

  const interfaceRecord = useGlobalAppStateStore().hooks.record.useDetails();
  const baseLocaleValue = InterfaceHelper.getDefaultLocale(interfaceRecord) ?? fallbackLng;

  if (!localeOptions || localeOptions?.length <= 1) return null;

  if (localeOptions.length === 2) {
    const otherLocale =
      localeOptions.find((locale) => locale.value !== baseLocaleValue) ?? localeOptions[1];
    const isActive = currentLocale === otherLocale.value;

    return (
      <Stack
        alignItems="center"
        className="!py-md ps-sm pe-sm mx-lg my-sm gap-x-md"
        direction="row"
      >
        <SvgTranslate02 className="text-icon-secondary" height={20} width={20} />
        <Typography className="flex-1" color="text-secondary" variant="text-sm" weight="medium">
          {t('blocks:Navigation.LanguageToggle', { otherLocale: otherLocale.label })}
        </Typography>
        <Toggle
          checked={isActive}
          onChange={(e) => {
            onSelect(e.target.checked ? otherLocale.value : baseLocaleValue);
          }}
          size="sm"
        />
      </Stack>
    );
  }

  const currentLocaleLabel = localeOptions.find((locale) => locale.value === currentLocale)?.label;

  return (
    <Popover triggerType="hover">
      <Popover.Button>
        <Stack
          alignItems="center"
          className="hover:bg-secondary_hover !py-md ps-sm pe-sm mx-lg my-sm cursor-pointer rounded-sm gap-x-md"
          direction="row"
        >
          <SvgTranslate02 className="text-icon-secondary" height={20} width={20} />
          <Typography className="flex-1" color="text-secondary" variant="text-sm" weight="medium">
            {t('blocks:Navigation.LanguageLabel', { label: currentLocaleLabel })}
          </Typography>
          <ChevronRight className="text-icon-tertiary" height={16} width={16} />
        </Stack>
      </Popover.Button>
      <Popover.Content
        minWidth={15}
        offset={4}
        placement={direction === 'rtl' ? 'left-start' : 'right-start'}
      >
        <Stack className="gap-y-xxs p-xs max-h-11xl overflow-auto" direction="column">
          {localeOptions.map(({ value, label }) => (
            <LanguageOption
              isActive={currentLocale === value}
              key={value}
              label={label}
              onSelect={onSelect}
              value={value}
            />
          ))}
        </Stack>
      </Popover.Content>
    </Popover>
  );
}
